import { useEffect, useMemo, useState } from "react"
import { useCustomizationFields } from "components/ui/customWebviewer/shared/CustomizationSection/useCustomizationFields.hook"
import type { TEref } from "types/elevationReferences.type"
import type { TCustomModelNumber } from "types/customModelNumbers.type"

/**
 * Form state of the "add custom object" panel (#1371). Nothing here is persisted as it changes:
 * unlike the edit sidebar, which commits every field on blur, this form is only read once, when
 * Create object is pressed.
 *
 * There is no category state at all — a custom object is by definition the one with no category on
 * any level, same as "Other". Unlike "Other", it has no name/price fields of its own either: both
 * come from the linked custom model number, picked through `SelectCustomModelNumberModal`.
 *
 * `pageErefs` are the elevation references of the page the area was drawn on. Erefs are page-bound
 * and the endpoint rejects a cross-page link, so a re-draw on another page has to drop a selection
 * that no longer belongs.
 */
export const useCreateCustomForm = (pageErefs: TEref[]) => {
	const [selectedErefId, setSelectedErefId] = useState<number | null>(null)
	const [width, setWidth] = useState<number | null>(null)
	const [height, setHeight] = useState<number | null>(null)
	const [depth, setDepth] = useState<number | null>(null)
	const [selectedCustomModelNumber, setSelectedCustomModelNumber] = useState<TCustomModelNumber | null>(null)
	// REQ-413/414. Read once, at Create, like everything else here.
	const customization = useCustomizationFields()

	// Drop an elevation that isn't on the page the area now sits on (a re-draw can move it).
	useEffect(() => {
		if (selectedErefId === null) return
		if (pageErefs.some((eref) => eref.id === selectedErefId)) return
		setSelectedErefId(null)
	}, [pageErefs, selectedErefId])

	const selectedEref = useMemo(
		() => pageErefs.find((eref) => eref.id === selectedErefId) ?? null,
		[pageErefs, selectedErefId],
	)

	// Unlike the cabinet/other panels, the elevation link is mandatory here (per the issue: "as for
	// any object" — a deviation the design agreed on for this flow specifically). Dimensions must be
	// GREATER than zero, not merely filled — inches of a real object, where 0 is not a smaller object
	// but a broken one, same rule the other create panels already enforce.
	const isValid =
		selectedErefId !== null &&
		selectedCustomModelNumber !== null &&
		width !== null &&
		width > 0 &&
		height !== null &&
		height > 0 &&
		depth !== null &&
		depth > 0

	return {
		selectedErefId,
		setSelectedErefId,
		selectedEref,
		width,
		setWidth,
		height,
		setHeight,
		depth,
		setDepth,
		selectedCustomModelNumber,
		selectCustomModelNumber: setSelectedCustomModelNumber,
		customization,
		isValid,
	}
}
